/**
 * Inject a message as though a partner had sent it.
 *
 * `POST /mimic/inbound` opens a real loopback MLLP connection into our own
 * listener, so framing, MSH parsing, channel dispatch, acknowledgment and
 * storage all run exactly as for a live arrival. It is not a fake row in the
 * log — it is the whole receive path, which is what makes this screen
 * demonstrable with no tunnel and no MedStar.
 *
 * Records come back flagged `mimic: true` and the log badges them. Injected
 * traffic must never be presentable as real.
 *
 * Note the endpoint parses the MSH and rejects with 400 BEFORE dialling, so a
 * deliberately malformed message cannot be used to produce an unparsed record
 * here; it just reports the parse failure.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { dataLayerApi } from '@/api/dataLayerApi'
import { toWireEr7, wireSegments } from '@/lib/dataLayer/er7'
import type { DataLayerFailure } from '@/lib/dataLayer/errors'
import type { Hl7InboundChannel, Hl7MimicResult } from '@/lib/dataLayer/types'

// Fallback while the fixture list loads; names double as types for these three.
const FIXTURES = ['ADT', 'ORM', 'ORU']

export function InjectMessageDialog({
  open,
  onOpenChange,
  channels,
  onInjected,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  channels: Hl7InboundChannel[]
  onInjected: () => void
}) {
  const [channelId, setChannelId] = useState('')
  const [fixture, setFixture] = useState('ADT')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<DataLayerFailure | null>(null)
  const [result, setResult] = useState<Hl7MimicResult | null>(null)

  const selected = channelId || channels[0]?.id || ''

  const { data: fixtures } = useQuery({
    queryKey: ['data-layer', 'fixtures'],
    queryFn: () => dataLayerApi.listFixtures(),
    retry: false,
  })

  // Both the request body and the "is a message pasted" test key off the
  // normalized value, so a whitespace-only paste cannot disable the fixture
  // selector while the body quietly falls back to the fixture.
  const wire = toWireEr7(message)
  const segments = wireSegments(message)

  async function inject() {
    if (!selected) return
    setBusy(true)
    setFailure(null)
    setResult(null)

    // Exactly one of message/fixture — the contract's oneOf rejects both.
    const body = wire ? { channelId: selected, message: wire } : { channelId: selected, fixture }

    const outcome = await dataLayerApi.mimicInbound(body)
    setBusy(false)

    if (!outcome.ok) {
      setFailure(outcome.failure)
      return
    }
    setResult(outcome.value)
    onInjected()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Inject a test message</DialogTitle>
          <DialogDescription>
            Delivers over a real loopback MLLP connection into our own listener, so the whole receive
            path runs. The record is flagged as simulated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="inject-channel">Inbound channel</Label>
            <Select value={selected} onValueChange={setChannelId}>
              <SelectTrigger id="inject-channel">
                <SelectValue placeholder="Pick a channel" />
              </SelectTrigger>
              <SelectContent>
                {channels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.id} · {channel.types.join(', ')} · :{channel.port}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inject-fixture">Sample message</Label>
            <Select value={fixture} onValueChange={setFixture} disabled={wire.length > 0}>
              <SelectTrigger id="inject-fixture">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* Keyed by NAME, not type — see SendPanel: duplicate values break the Select. */}
                {fixtures?.fixtures.map((f) => (
                  <SelectItem key={f.name} value={f.name}>
                    {f.name} · {f.description}
                  </SelectItem>
                )) ??
                  FIXTURES.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Ignored if you paste a message below.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inject-message">Or paste an HL7 message</Label>
            <Textarea
              id="inject-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="MSH|^~\&|EPIC|MEDSTAR|T21|AWS|..."
              className="min-h-[120px] font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              {segments.length > 0 && (
                <>
                  {segments.length} segment{segments.length === 1 ? '' : 's'}.{' '}
                </>
              )}
              Line breaks typed here become CR (0x0D) on the wire.
            </p>
          </div>

          {failure && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {failure.message}
            </p>
          )}

          {result && (
            <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5 text-sm">
              <p className="font-medium">
                Delivered · acknowledged {result.ackCode}
              </p>
              <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                control id {result.controlId}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={inject} disabled={busy || !selected}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
            Inject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
